import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { GET_INVITATION_INFO, ACCEPT_INVITATION } from '../graphql/settings';
import { event } from '../analytics';

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const { data, loading, error } = useQuery(GET_INVITATION_INFO, {
    variables: { token },
    skip: !token,
  });
  
  const [acceptInvitation, { loading: accepting }] = useMutation(ACCEPT_INVITATION);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  
  const [formError, setFormError] = useState('');
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    
    // Validation
    if (!formData.username.trim()) {
      setFormError('Username is required');
      return;
    }
    
    if (formData.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }
    
    try {
      await acceptInvitation({
        variables: {
          input: {
            token,
            username: formData.username,
            password: formData.password,
            firstName: formData.firstName || null,
            lastName: formData.lastName || null,
          },
        },
      });
      event('accept_invite', { event_category: 'auth', event_action: 'accept_invite' });
      // Redirect to dashboard
      navigate('/dashboard');
      window.location.reload(); // Refresh to update auth state
    } catch (err: any) {
      setFormError(err.message || 'Failed to accept invitation');
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <Alert severity="error">Error loading invitation</Alert>
      </Box>
    );
  }
  
  const invitationInfo = data?.invitationInfo;
  
  if (!invitationInfo?.isValid) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 2
      }}>
        <Card sx={{ maxWidth: 500, width: '100%' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h5" color="error" gutterBottom>
              Invalid Invitation
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {invitationInfo?.errorMessage || 'This invitation link is not valid.'}
            </Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }
  
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'error';
      case 'editor': return 'primary';
      case 'viewer': return 'default';
      default: return 'default';
    }
  };
  
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      bgcolor: '#f5f5f5',
      p: 2
    }}>
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e3a5f', mb: 1 }}>
              Verit Compliance
            </Typography>
            <Typography variant="h6" gutterBottom>
              You've been invited!
            </Typography>
            <Typography color="text.secondary">
              {invitationInfo.invitedByUsername} has invited you to join
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>
              {invitationInfo.companyName}
            </Typography>
            <Chip 
              label={`as ${invitationInfo.role.charAt(0).toUpperCase() + invitationInfo.role.slice(1)}`}
              color={getRoleColor(invitationInfo.role) as any}
              sx={{ mt: 1 }}
            />
          </Box>
          
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Create your account to join
            </Typography>
            
            <TextField
              fullWidth
              label="Email"
              value={invitationInfo.email}
              disabled
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
              sx={{ mb: 2 }}
              disabled={accepting}
            />
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={accepting}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                disabled={accepting}
              />
            </Box>
            
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              sx={{ mb: 2 }}
              disabled={accepting}
              helperText="At least 8 characters"
            />
            
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              required
              sx={{ mb: 3 }}
              disabled={accepting}
            />
            
            <Button
              data-ga-label="Accept invitation submit"
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={accepting}
              sx={{ 
                bgcolor: '#1e3a5f',
                '&:hover': { bgcolor: '#152a4a' },
                py: 1.5
              }}
            >
              {accepting ? <CircularProgress size={24} color="inherit" /> : 'Accept Invitation & Create Account'}
            </Button>
          </Box>
          
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Button 
                variant="text" 
                onClick={() => navigate('/login')}
                sx={{ textTransform: 'none' }}
              >
                Login
              </Button>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AcceptInvite;
